import React, { useMemo } from 'react';
import { FieldArray, Form, Formik, FormikErrors, FormikHelpers, FormikProps, FormikState } from 'formik';
import {
  SalvBanner,
  SalvBannerType,
  SalvBannerAccent,
  DatePicker,
  SalvButton,
  SalvFieldHeader,
  SalvModal,
  SalvNumberField,
  SalvRadioToggleButton,
  SalvRadioToggleGroup,
  SalvSwitch,
  SalvTextArea,
  Size,
} from '@salvapp/ui-components';

import { useDispatch } from 'react-redux';

import './ScenarioVersionTestModal.scss';
import { scenarioTestingApi, scenarioVersionInternalApi } from '../../../apis';
import { parseErrorMessage } from '../../../common/errors/helpers';
import { showFeedback } from '../../../feedback/feedbackReducer';
import { faXmarkLarge } from '@fortawesome/pro-solid-svg-icons';
import { faPlus } from '@fortawesome/pro-regular-svg-icons/faPlus';
import dayjs, { Dayjs } from 'dayjs';
import { useQuery } from '@tanstack/react-query';
import {
  InternalApiScenario,
  InternalApiScenarioContentForAggregationsDetectionLanguageEnum,
  InternalApiScenarioContentTestResultInformation,
  InternalApiScenarioEntityType,
  InternalApiScenarioIntervalTimeUnit,
  InternalApiScenarioLanguage,
  InternalApiScenarioTypeV2,
  InternalApiScenarioVersionParameter,
  InternalApiScenarioVersionStatus,
  InternalApiStartScenarioVersionTest,
} from '../../../api-internal';
import { useMe } from '../../../organisation/useMe';
import { suppressionIntervalToDays } from '../../../common/versioning/helper';
import { SCENARIO_VERSION_QUERY_KEY } from '../scenarioVersionActions';
import * as Yup from 'yup';
import { generatePeriodicStartTimes } from './helper';
import { DATE_TIME_FORMAT, dateTimeFormatRegex } from '../../../common/helper';
import { ParameterValuesView } from '../version-modal/ParametersEditor';
import Editor from '../../../common/editor/Editor';
import TriggeringSegments from '../../segments/TriggeringSegments';

export const USER_DEFINED_ALERTING_INTERVALS_LIMIT = 4; // number of intervals a user can add (+1 default from scenario)

export interface VersionTestModalFormValues {
  dateTimeFrom: string;
  dateTimeTo: string;
  periodicScenarioInterval: number;
  periodicScenarioTimeUnit: InternalApiScenarioIntervalTimeUnit;
  suppressionIntervals: number[];
  onAlertedEntitiesOnly: boolean;
  parameters: InternalApiScenarioVersionParameter[];
  segments: string[];
  excludedSegments: string[];
  note?: string;
}

export interface VersionTestModalProps {
  scenario: InternalApiScenario;
  versionId: string | null;
  show: boolean;
  onComplete: () => void;
  onClose: () => void;
  testRunToDuplicate?: InternalApiScenarioContentTestResultInformation;
}

export const ScenarioVersionTestModal = ({
  scenario,
  versionId,
  show,
  onComplete,
  onClose,
  testRunToDuplicate,
}: VersionTestModalProps) => {
  const { isGlobalAdmin, currentOrganisation } = useMe();
  const { id: scenarioId, type, entityType } = scenario;
  const isPeriodic = type === InternalApiScenarioTypeV2.PERIODIC;
  const isPeriodicEntity = type === InternalApiScenarioTypeV2.PERIODIC_ENTITY;
  const isAnyPeriodicType = isPeriodic || isPeriodicEntity;
  const isTransactionScenario = entityType === InternalApiScenarioEntityType.TRANSACTION;

  const validationSchema = Yup.object().shape({
    dateTimeFrom: Yup.string()
      .required('Start time is required')
      .matches(dateTimeFormatRegex, DATE_TIME_FORMAT)
      .test('is-before', 'Start time must be before end time', function (value) {
        return dayjs(value, DATE_TIME_FORMAT).isBefore(dayjs(this.parent.dateTimeTo, DATE_TIME_FORMAT));
      }),
    dateTimeTo: Yup.string().required('End time is required').matches(dateTimeFormatRegex, DATE_TIME_FORMAT),
    ...(isAnyPeriodicType && {
      periodicScenarioInterval: Yup.number()
        .required('Periodic interval is required')
        .positive('Interval must be positive'),
      periodicScenarioTimeUnit: Yup.string().required('Interval time unit is required'),
    }),
    suppressionIntervals: Yup.array().of(Yup.number().min(0, 'Interval cannot be negative')),
    onAlertedEntitiesOnly: Yup.boolean(),
  });

  const { data: version } = useQuery({
    queryKey: [SCENARIO_VERSION_QUERY_KEY, versionId, currentOrganisation?.id],
    queryFn: async () =>
      await scenarioVersionInternalApi.getScenarioVersion({ scenarioId: scenarioId, id: versionId! }),
    enabled: !!versionId,
  });

  const dispatch = useDispatch();
  const isHistoric = version?.status === InternalApiScenarioVersionStatus.HISTORIC;
  const initialValues: VersionTestModalFormValues = testRunToDuplicate
    ? {
        dateTimeFrom: dayjs(testRunToDuplicate.testDataTimeFrom).format(DATE_TIME_FORMAT),
        dateTimeTo: dayjs(testRunToDuplicate.testDataTimeTo).format(DATE_TIME_FORMAT),
        periodicScenarioInterval: testRunToDuplicate.testPeriodicInterval?.interval || 1,
        periodicScenarioTimeUnit:
          testRunToDuplicate.testPeriodicInterval?.timeUnit || InternalApiScenarioIntervalTimeUnit.WEEK,
        suppressionIntervals: testRunToDuplicate.alertingIntervals || [0],
        onAlertedEntitiesOnly: testRunToDuplicate.onAlertedEntitiesOnly,
        parameters: testRunToDuplicate.testParameters || [],
        segments: testRunToDuplicate.testSegments || [],
        excludedSegments: testRunToDuplicate.testExcludedSegments || [],
        note: testRunToDuplicate.note,
      }
    : {
        dateTimeFrom: dayjs().subtract(1, 'days').format(DATE_TIME_FORMAT),
        dateTimeTo: dayjs().format(DATE_TIME_FORMAT),
        periodicScenarioInterval: version?.periodicScenarioAttributes?.periodicInterval || 1,
        periodicScenarioTimeUnit:
          version?.periodicScenarioAttributes?.periodicIntervalTimeUnit || InternalApiScenarioIntervalTimeUnit.WEEK,
        suppressionIntervals: [
          version?.suppressionInterval ? suppressionIntervalToDays(version.suppressionInterval) : 0,
        ],
        onAlertedEntitiesOnly: false,
        parameters: version?.parameters || [],
        segments: version?.segments || [],
        excludedSegments: version?.excludedSegments || [],
        note: version?.note,
      };

  const {
    data: { aggregations },
  } = useQuery({
    queryKey: ['version_content_aggregations', version?.query, currentOrganisation?.id],
    queryFn: async () =>
      await scenarioTestingApi.detectAggregations({
        scenarioHandle: scenarioId,
        internalApiScenarioContentForAggregationsDetection: {
          content: version!.query,
          language:
            version!.language === InternalApiScenarioLanguage.SQL
              ? InternalApiScenarioContentForAggregationsDetectionLanguageEnum.SQL
              : InternalApiScenarioContentForAggregationsDetectionLanguageEnum.JMESPATH,
        },
      }),
    initialData: { aggregations: [] },
    enabled: !!version,
  });

  const freshestAggregationDate = useMemo(() => {
    const aggregationDates = aggregations.filter((a) => a.dataStartTime !== undefined).map((a) => a.dataStartTime!);

    return aggregationDates.length
      ? aggregationDates.reduce((max, curr) => {
          if (curr > max) {
            return curr;
          }
          return max;
        })
      : undefined;
  }, aggregations);

  const initialFormikState: Partial<FormikState<VersionTestModalFormValues>> = {
    values: initialValues,
    isSubmitting: false,
    isValidating: false,
  };

  const testTimeRangeTitle = isAnyPeriodicType ? 'Testing time range' : 'Sample data';
  const testTimeRangeDescription = isAnyPeriodicType
    ? `Periodic test runs are executed between the selected time range`
    : isTransactionScenario
      ? 'Sample data set is based on transaction timestamp'
      : 'Sample data set is based on person create or update times';

  const scenarioTestConfigs = (
    values: VersionTestModalFormValues,
    errors: FormikErrors<VersionTestModalFormValues>,
    setFieldValue: FormikHelpers<VersionTestModalFormValues>['setFieldValue']
  ) => (
    <>
      <div>
        <SalvFieldHeader label={testTimeRangeTitle} description={testTimeRangeDescription} />
        <div className='d-flex'>
          <div className='m-r-8'>
            <div className='text-strong-s m-b-8'>From</div>
            <DatePicker
              size='small'
              showTime
              value={
                dayjs(values.dateTimeFrom, DATE_TIME_FORMAT).isValid()
                  ? dayjs(values.dateTimeFrom, DATE_TIME_FORMAT)
                  : null
              }
              onChange={(dateTimeFrom: Dayjs | null) => {
                setFieldValue('dateTimeFrom', dateTimeFrom?.format(DATE_TIME_FORMAT));
              }}
              isClearable={false}
              min={isGlobalAdmin ? undefined : dayjs().subtract(1, 'year')}
              max={dayjs()}
              isInvalid={!!errors.dateTimeFrom}
            />
            <div className='text-secondary text-xs m-t-8'>Local timezone</div>
          </div>
          <div>
            <div className='text-strong-s m-b-8'>To</div>
            <DatePicker
              size='small'
              showTime
              value={
                dayjs(values.dateTimeTo, DATE_TIME_FORMAT).isValid() ? dayjs(values.dateTimeTo, DATE_TIME_FORMAT) : null
              }
              onChange={(dateTimeTo: Dayjs | null) => {
                setFieldValue('dateTimeTo', dateTimeTo?.format(DATE_TIME_FORMAT));
              }}
              isClearable={false}
              min={dayjs().subtract(1, 'year')}
              max={dayjs()}
              isInvalid={!!errors.dateTimeTo}
            />
          </div>
        </div>
        {freshestAggregationDate && (
          <SalvBanner
            accent={SalvBannerAccent.LIGHT}
            className='flex-grow-1 m-t-8'
            message={`The oldest aggregation data that can be tested is ${dayjs(freshestAggregationDate).format('YYYY-MM-DD')}`}
            type={SalvBannerType.WARNING}
          />
        )}
      </div>
      {isAnyPeriodicType && (
        <div>
          <SalvFieldHeader label='Periodic scenario interval' />
          <div className='d-flex flex-wrap periodic-scenario-interval'>
            <SalvNumberField
              ariaLabel='Periodic scenario interval'
              size={Size.SMALL}
              value={values.periodicScenarioInterval}
              onChange={(value) => {
                setFieldValue('periodicScenarioInterval', value);
              }}
              className='m-r-8'
              isInvalid={!!errors.periodicScenarioInterval}
            />
            <SalvRadioToggleGroup
              ariaLabel='Periodic scenario interval time unit'
              defaultValue={values?.periodicScenarioTimeUnit}
              size={Size.SMALL}
              onChange={(periodicScenarioTimeUnit) =>
                setFieldValue(
                  'periodicScenarioTimeUnit',
                  periodicScenarioTimeUnit as InternalApiScenarioIntervalTimeUnit
                )
              }
            >
              {!isPeriodicEntity && (
                <SalvRadioToggleButton label='Hour' value={InternalApiScenarioIntervalTimeUnit.HOUR} />
              )}
              <SalvRadioToggleButton label='Day' value={InternalApiScenarioIntervalTimeUnit.DAY} />
              <SalvRadioToggleButton label='Week' value={InternalApiScenarioIntervalTimeUnit.WEEK} />
              <SalvRadioToggleButton label='Month' value={InternalApiScenarioIntervalTimeUnit.MONTH} />
            </SalvRadioToggleGroup>
          </div>
        </div>
      )}
      <FieldArray name='suppressionIntervals'>
        {({ replace, remove, push }) => {
          return (
            <div>
              <SalvFieldHeader
                size={Size.MEDIUM}
                label='Alert suppression interval'
                description='Interval for triggering alerts (days)'
              />

              {values.suppressionIntervals.map((suppressionInterval, index) => {
                return (
                  <div className='d-flex align-items-center m-b-8' key={index}>
                    <SalvNumberField
                      className='suppression-interval-field'
                      minValue={0}
                      size={Size.SMALL}
                      ariaLabel={`description-${index + 1}`}
                      value={suppressionInterval}
                      onChange={(value) => replace(index, value)}
                      disabled={index === 0}
                    />

                    {index !== 0 && (
                      <SalvButton
                        className='m-l-8'
                        icon={faXmarkLarge}
                        variant='muted'
                        size={Size.SMALL}
                        onClick={() => remove(index)}
                      />
                    )}
                  </div>
                );
              })}

              <SalvButton
                icon={faPlus}
                label='Add interval'
                size={Size.SMALL}
                variant='muted'
                onClick={() => push(0)}
                disabled={values.suppressionIntervals.length >= USER_DEFINED_ALERTING_INTERVALS_LIMIT}
              />
            </div>
          );
        }}
      </FieldArray>
      {!!values.parameters.length && (
        <div>
          <SalvFieldHeader size={Size.MEDIUM} label='Test parameters' />
          <ParameterValuesView disabled={false} />
        </div>
      )}

      {!isPeriodic && (
        <TriggeringSegments
          isDisabled={false}
          isTesting={true}
          isTransactionScenario={isTransactionScenario}
          isPeriodicEntity={isPeriodicEntity}
          withDescription={false}
        />
      )}

      {
        <SalvTextArea
          label='Test run note'
          description='Comment on what is tested'
          placeholder='Note'
          size={Size.SMALL}
          onChange={(note) => setFieldValue('note', note)}
          value={values.note}
          autoSizing={true}
          minRows={2}
          isInvalid={!!errors.note}
        />
      }

      {isAnyPeriodicType && generatePeriodicStartTimes(values)}
      {!isPeriodic && (
        <div className='d-flex align-items-center'>
          <SalvSwitch
            className='m-r-8'
            selected={values.onAlertedEntitiesOnly}
            onChange={() => setFieldValue('onAlertedEntitiesOnly', !values.onAlertedEntitiesOnly)}
          />

          <SalvFieldHeader
            size={Size.SMALL}
            label='Run on alerted entities only'
            description='Run the test only on entities alerted by this scenario within the selected time range'
          />
        </div>
      )}
    </>
  );

  const submit = async (
    formValues: VersionTestModalFormValues,
    { setSubmitting }: FormikHelpers<VersionTestModalFormValues>
  ) => {
    const internalApiStartScenarioVersionTest: InternalApiStartScenarioVersionTest = {
      testDataTimeFrom: dayjs(formValues.dateTimeFrom, DATE_TIME_FORMAT).toDate(),
      testDataTimeTo: dayjs(formValues.dateTimeTo, DATE_TIME_FORMAT).toDate(),
      ...(isAnyPeriodicType && {
        periodicInterval: {
          interval: formValues.periodicScenarioInterval,
          timeUnit: formValues.periodicScenarioTimeUnit.toString() as InternalApiScenarioIntervalTimeUnit,
        },
      }),
      alertingIntervals: formValues.suppressionIntervals.sort((a, b) => a - b),
      onAlertedEntitiesOnly: formValues.onAlertedEntitiesOnly,
      testParameters: formValues.parameters,
      testSegments: formValues.segments,
      testExcludedSegments: formValues.excludedSegments,
      note: formValues.note,
    };

    try {
      if (versionId) {
        await scenarioTestingApi.startVersionTest({
          scenarioHandle: scenarioId,
          handle: versionId,
          internalApiStartScenarioVersionTest,
        });
      }
      onComplete();
    } catch (e: unknown) {
      const message = await parseErrorMessage(e);
      dispatch(
        showFeedback({
          message,
          success: false,
        })
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Formik
      enableReinitialize={true}
      initialValues={initialValues}
      onSubmit={submit}
      validationSchema={validationSchema}
    >
      {({
        isSubmitting,
        resetForm,
        values,
        errors,
        setFieldValue,
        submitForm,
      }: FormikProps<VersionTestModalFormValues>) => {
        const handleClose = () => {
          resetForm(initialFormikState);
          onClose();
        };
        return (
          <Form>
            <SalvModal show={show && !!version} onHide={handleClose} className='scenario-version-testing-modal'>
              {version && (
                <SalvModal.Header>New test run for Scenario version ID: {version.versionId}</SalvModal.Header>
              )}

              <SalvModal.Body>
                {version && (
                  <div className='d-flex'>
                    <div className='scenario-test-left-panel'>{scenarioTestConfigs(values, errors, setFieldValue)}</div>
                    <div className='scenario-test-right-panel'>
                      <Editor
                        disabled={true}
                        value={version.query}
                        onChange={() => {}}
                        type={InternalApiScenarioLanguage.SQL}
                        className='editor'
                        syntaxHighlight={false}
                      />
                    </div>
                  </div>
                )}
              </SalvModal.Body>
              <SalvModal.Footer>
                <SalvButton onClick={handleClose} variant='muted' label='Dismiss' className='m-a-0 m-x-8'></SalvButton>
                <SalvButton
                  disabled={isSubmitting || isHistoric}
                  type='submit'
                  variant='primary'
                  label='Start test run'
                  className='m-a-0 m-l-8'
                  onClick={submitForm}
                />
              </SalvModal.Footer>
            </SalvModal>
          </Form>
        );
      }}
    </Formik>
  );
};

export default ScenarioVersionTestModal;
